// Must be imported before any component stylesheet. Vite emits CSS in import
// order, and the base/primitive layer has to lose to component overrides of
// equal specificity (e.g. `.ss-btn { display: inline-flex }` vs a component's
// `display: none` in a media query).
import "~/index.scss";

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
} from "@remix-run/react";

import BookingModal from "~/components/booking/BookingModal";
import CookieConsent from "~/components/consent/CookieConsent";
import GatedScripts from "~/components/consent/GatedScripts";
import Footer from "~/components/layout/Footer";
import Header from "~/components/layout/Header";
import { SUPPORTED_LANGUAGES } from "~/config/constants";
import { BookingProvider } from "~/context/BookingContext";
import { ConsentProvider } from "~/context/ConsentContext";
import { CountryProvider } from "~/context/CountryContext";
import {
  LanguageContextType,
  LanguageProvider,
} from "~/context/LanguageContext";
import common from "~/i18n/common";

import RootErrorBoundary from "./root.errorBoundary";
import RootLoader, { RootLoaderData } from "./root.loader";
import RootMeta from "./root.meta";
import OrganizationSchema from "./seo/OrganizationSchema";

export const ErrorBoundary = RootErrorBoundary;
export const meta = RootMeta;
export const loader = RootLoader;

export function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  let lang = pathname.split("/")[1] as LanguageContextType["lang"];
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
    lang = "en";
  }

  // The loader never ran if we are rendering an error, so its data is absent.
  const error = useRouteError();
  let countryCode = "us";
  let env: RootLoaderData["ENV"] = {};
  if (!error) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const data = useLoaderData<RootLoaderData | undefined>();
    if (data) {
      countryCode = data.countryCode;
      env = data.ENV;
    }
  }

  const isRtl = lang === "ar";
  const t = common[lang];

  return (
    <html lang={lang} dir={isRtl ? "rtl" : "ltr"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#FAF8F3" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <OrganizationSchema />
        <Meta />
        <Links />
      </head>
      <body>
        <a className="ss-skip-link" href="#main-content">
          {t.nav.skipToContent}
        </a>

        <CountryProvider country={countryCode}>
          <LanguageProvider lang={lang}>
            <ConsentProvider>
              <BookingProvider>
                <Header />
                {children}
                <Footer />
                <BookingModal />
                <CookieConsent />
                <GatedScripts />
              </BookingProvider>
            </ConsentProvider>
          </LanguageProvider>
        </CountryProvider>

        <script
          // Public runtime config. Not a secret: the Sentry DSN ships in every
          // browser SDK by design.
          dangerouslySetInnerHTML={{
            __html: `window.ENV=${JSON.stringify(env).replace(/</g, "\\u003c")}`,
          }}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}
